import * as ExcelJS from "exceljs";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { getTransactionNames } from "src/components/extractPdf";
import transactionLabel from "src/utils/transactionLabel";
import { ExtractItem } from "src/utils/extractDescription";
import groupTransactionsByDate from "src/utils/groupTransactionsByDate";
import moment from "moment";
import toLocaleAmount from "src/utils/toLocaleAmount";
import { documentFormatted } from "src/utils/documentFormatted";
import axios from "axios";
import { currencyFormat } from "../currencyFormat";
import getTransactionDocuments from "../getTransactionDocuments";

const dateFormat = (date: string | undefined): string => {
  const formattedDate = format(new Date(date || ""), "dd/MM/yyyy HH:mm", {
    locale: ptBR,
  });
  return formattedDate;
};

export default async function handleExportExcelMember(
  items: ExtractItem[],
  userData: {
    name: string;
    number: string;
    branch: string;
    document: string;
    balance: number;
  },
  isClient?: boolean
) {
  const token = window.localStorage.getItem(
    isClient ? "@stricv2:token" : "@backoffice:token"
  );

  const data = groupTransactionsByDate(items);

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/transactions/daily-final-balances`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const dailyBalances = response.data[0]?.dailyBalances?.map(
      (balance: { amount: number; createdAt: string }) => {
        return {
          amount: balance.amount,
          createdAt: moment(balance.createdAt.split("T")[0]).format(
            "DD/MM/YYYY"
          ),
        };
      }
    );

    const update_date = data.map((item) => {
      const matchedDaily = dailyBalances?.find(
        (daily: { createdAt: string }) => daily.createdAt === item.date
      );

      const amount = matchedDaily ? matchedDaily.amount : 0;

      return {
        ...item,
        dayly_amount: amount,
      };
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Extrato conta corrente");

    // Definindo largura das colunas
    const columnA = worksheet.getColumn("A");
    const columnB = worksheet.getColumn("B");
    const columnC = worksheet.getColumn("C");
    const columnD = worksheet.getColumn("D");
    const columnE = worksheet.getColumn("E");
    const columnF = worksheet.getColumn("F");
    columnA.width = 30;
    columnB.width = 55;
    columnC.width = 35;
    columnD.width = 30;
    columnE.width = 20;
    columnF.width = 30;

    worksheet.columns.forEach((column) => {
      column.alignment = {
        vertical: "middle",
      };
    });

    // Adicionando logo na primeira linha
    const imageId = workbook.addImage({
      base64: logo64,
      extension: "png",
    });

    const imageWidth = 250;
    const imageHeight = 50;

    worksheet.addImage(imageId, {
      tl: { col: 0.6, row: 0.5 },
      ext: { width: imageWidth, height: imageHeight },
    });

    worksheet.properties.defaultRowHeight = 30;

    worksheet.mergeCells("A1:B2");

    worksheet.getCell("C1").value = userData.name;
    worksheet.getCell("D1").value = `CNPJ/CPF: ${userData.document}`;
    worksheet.getCell("E1").value = `Saldo na conta: ${currencyFormat(
      userData.balance
    )}`;
    worksheet.getCell("C2").value = `Agência: ${userData.branch}`;
    worksheet.getCell("D2").value = `Conta: ${userData.number}`;

    worksheet.addRow([""]);
    worksheet.addRow(["Extrato conta corrente"]).eachCell((cell) => {
      cell.style = {
        alignment: { vertical: "middle", horizontal: "left" },
        font: { bold: true, size: 16 },
      };
    });

    const initialDate = update_date[update_date.length - 1].date;
    const finalDate = update_date[0].date;
    const today = moment().format("DD/MM/YYYY HH:mm");

    worksheet
      .addRow([
        "Período de visualização:",
        `${initialDate} até ${finalDate}`,
        "",
        "",
        "Emitido dia:",
        today,
      ])
      .eachCell((cell) => {
        cell.font = {
          size: 12,
          color: { argb: "000000" },
          name: "Calibri",
          scheme: "minor",
          family: 2,
        };
      });

    worksheet.addRow([""]);

    update_date.forEach((groupedTransactions, index) => {
      worksheet
        .addRow(["Lançamentos", groupedTransactions.date, "", "", "", ""])
        .eachCell((cell) => {
          cell.style = {
            font: {
              size: 16,
              color: { argb: "000000" },
              name: "Calibri",
              scheme: "minor",

              bold: true,
              family: 2,
            },
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "cccccc" },
            },
            alignment: { vertical: "middle" },
          };
        });

      worksheet
        .addRow([
          "Data",
          "Lançamento",
          "Documento",
          "Order ID",
          "Valor",
          "Saldo",
        ])
        .eachCell((cell) => {
          cell.font = {
            size: 16,
            color: { argb: "bf9000" },
            name: "Calibri",
            scheme: "minor",
            bold: true,
            family: 2,
          };
        });

      groupedTransactions.transactions.forEach((item, index) => {
        if (item.status == "done") {
          const names = getTransactionNames(item);
          const documents = getTransactionDocuments(item, true);

          worksheet
            .addRow([
              dateFormat(item.created_at),
              `${transactionLabel(item)} ${
                item.direction == "in"
                  ? `de ${names.payerName}`
                  : `para ${names.beneficiaryName}`
              }`,
              `${
                item.direction == "in" ? documents.payer : documents.beneficiary
              }`,
              `${
                item.order_id
                  ? "*****" +
                    item.order_id.slice(
                      item.order_id.length - 7,
                      item.order_id.length
                    )
                  : "#######"
              }`,
              `${item.direction == "in" ? "+" : "-"} ${currencyFormat(
                Number(item.amount)
              )}`,
              currencyFormat(item.newValue),
            ])
            .eachCell((cell) => {
              if (cell.address.includes("E")) {
                cell.font = {
                  color:
                    item.direction == "in"
                      ? { argb: "4AD858" }
                      : { argb: "B92100" },
                };
              }
            });
        }
      });
    });

    // Escrevendo o arquivo Excel e criando o link para download
    const excelBlob = await workbook.xlsx.writeBuffer();
    const blob = new Blob([excelBlob], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const timestamp = moment().format("YYYY/MM/DD_HH:mm");

    // Defina um nome de arquivo válido
    const fileName = `${userData.name.replace(" ", "_")}_${timestamp}.xlsx`;
    a.download = fileName;

    // Acione o download
    a.click();
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}

const logo64 =
  "data:image/png;base64,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";
